import { Navigate, useLocation, useParams } from 'react-router-dom';

import { Button } from 'component-lib/atoms/button/Button';
import { ReactComponent as AccessDeniedIllustration } from 'create/atoms/authorization/AccessDenied.svg';
import { useAuthUserContext } from 'support/auth/AuthUserContext';
import { UserSegment } from 'types/graphql';

import { TeamResourceName } from './ResourceName';
import { ActionName, useHasAccess } from './useHasAccess';

export interface ProtectedUserSegmentRouteProps {
  segment: UserSegment;
  children: JSX.Element;
}

/**
 * Route only visible for users with the specified segment
 */
export const ProtectedUserSegmentRoute = ({ segment, children }: ProtectedUserSegmentRouteProps) => {
  const { instruqtUser } = useAuthUserContext();

  if (instruqtUser.segments.includes(segment)) {
    return children;
  }

  return <AccessDenied />;
};

export interface ProtectedRouteProps {
  action: ActionName;
  teamID?: string;
  teamSlug?: string;
  children: JSX.Element;
}

/**
 * Route only visible to user's who have permission to perform the given action on the target resource
 */
export const ProtectedRoute = ({ action, teamID, teamSlug: teamSlugProp, children }: ProtectedRouteProps) => {
  const { teamSlug: teamSlugRouteParam } = useParams<'teamSlug'>();

  const resourceName = new TeamResourceName(teamID, teamSlugProp || teamSlugRouteParam);

  const { hasAccess } = useHasAccess(action, resourceName);

  if (hasAccess) {
    return children;
  }

  return <AccessDenied />;
};

function AccessDenied() {
  const { pathname, search } = useLocation();
  const { isAnonymous } = useAuthUserContext();

  if (isAnonymous) {
    return <Navigate replace to={`/login?${new URLSearchParams([['from', `${pathname}${search}`]])}`} />;
  }

  return (
    <div className="flex items-center justify-center gap-4 py-20">
      <AccessDeniedIllustration />

      <div className="flex flex-col gap-3 items-start">
        <div className="flex flex-col gap-half">
          <h1 className="display-2">You're not on the VIP list... yet!</h1>
          <p>You don’t have permission to view this page. Ask your account admin for access.</p>
        </div>

        <Button to="/" variant="outline">
          Back to home screen
        </Button>
      </div>
    </div>
  );
}
